/**
 * Zoomed-out view of the deals map: one dot per section that holds
 * in-scope tracts, at the section centroid. Pure.
 *
 * Same rules as the tract shapes (tractShapes.js): the dot is coloured by
 * the most advanced LIVE status bucket among the section's deals, and dead
 * deals are left out entirely unless `includeDead` — so the counts in a dot
 * match what the tract shapes show once you zoom in. Counts and net acres
 * come from aggregateBySection (distinct deals; each aliquot's acres once).
 */
import { aggregateBySection } from "./trsAggregation";
import { polygonLabelPoint } from "./mapLabels";
import { TRACT_BUCKETS, mostAdvancedBucket, statusBucket } from "./tractStatusBucket";

export const DOT_MIN_RADIUS = 4;
export const DOT_MAX_RADIUS = 10;

/**
 * Circle radius (px) for a section's distinct deal count: 4 px for one deal,
 * growing with the square root, capped at 10 px (~ 10 deals).
 */
export const dotRadius = (dealCount) => {
  const n = Math.max(1, Number(dealCount) || 1);
  return Math.min(DOT_MAX_RADIUS, DOT_MIN_RADIUS + 2 * Math.sqrt(n - 1));
};

/**
 * @param {Array<object>} rows  joined tract rows
 * @param {Map<string, object>} sectionIndex  indexSectionsByTrs(sections)
 * @param {{ includeDead?: boolean }} [options]
 * @returns {object} FeatureCollection of Points; props:
 *   trs ("7N-7W-21"), bucket, deal_count, active_count, net_acres, radius,
 *   and n_<bucket> (distinct deals per bucket, for the hover breakdown)
 */
export const sectionDotFeatures = (rows, sectionIndex, options = {}) => {
  const shown = (Array.isArray(rows) ? rows : []).filter(
    (r) => options.includeDead || statusBucket(r?.deal_status) !== "dead"
  );
  const out = { type: "FeatureCollection", features: [] };
  if (!sectionIndex || typeof sectionIndex.get !== "function") return out;

  for (const [key, b] of aggregateBySection(shown).entries()) {
    const section = sectionIndex.get(key);
    const pt = polygonLabelPoint(section?.geometry);
    if (!pt) continue; // section polygon not loaded: nothing to anchor to
    const perBucket = Object.fromEntries(TRACT_BUCKETS.map((t) => [`n_${t.key}`, 0]));
    const buckets = [];
    for (const d of b.deals) {
      const k = statusBucket(d.deal_status);
      perBucket[`n_${k}`] += 1;
      buckets.push(k);
    }
    out.features.push({
      type: "Feature",
      geometry: { type: "Point", coordinates: pt },
      properties: {
        trs: key,
        bucket: mostAdvancedBucket(buckets),
        deal_count: b.count,
        active_count: b.openCount,
        net_acres: b.netAcres,
        radius: dotRadius(b.count),
        ...perBucket,
      },
    });
  }
  return out;
};
